import { useMutation, useQuery } from '@tanstack/react-query';
import { Edit2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { customerAPI, pucAPI } from '../api/index';
import { queryClient } from '../App';
import { Button } from '../components/Button';
import { ExpiryBadge } from '../components/ExpiryBadge';
import AddPUCModal from '../components/modals/AddPUCModal';
import { PageHeader } from '../components/PageHeader';
import { useDebounce } from '../hooks/useDebounce';
import { daysRemaining, formatDate } from '../lib/utils';
import type { PUC } from '../types/index';

export function PUCsPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<'all' | 'active' | 'expiring_soon' | 'expired'>('all')
  const [offset, setOffset] = useState(0)
  const [search, setSearch] = useState('')
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [showCustomerPicker, setShowCustomerPicker] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedCustomerId, setSelectedCustomerId] = useState('')
  const [selectedPuc, setSelectedPuc] = useState<PUC | undefined>()
  const [customerSearch, setCustomerSearch] = useState('')

  const debouncedSearch = useDebounce(search, 300)

  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['customers', 'puc-picker'],
    queryFn: () =>
      customerAPI.list({
        limit: 200,
        offset: 0,
        active: true,
      }).then((r) => r.data),
  })

  const customers = customersData?.customers || []
  const filteredCustomers = customers.filter((customer) =>
    `${customer.full_name} ${customer.phone}`.toLowerCase().includes(customerSearch.toLowerCase())
  )

  const { data, isLoading } = useQuery({
    queryKey: ['pucs', status, offset, debouncedSearch],
    queryFn: () =>
      pucAPI.list({
        status: status === 'all' ? undefined : status,
        limit: 20,
        offset,
      }).then(r => r.data),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => pucAPI.delete(id),
    onSuccess: () => {
      toast.success('PUC deleted')
      queryClient.invalidateQueries({ queryKey: ['pucs'] })
      setDeleteId(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to delete')
    },
  })

  const total = data?.total || 0
  const pucs = data?.pucs || []
  const totalPages = Math.ceil(total / 20)
  const currentPage = Math.floor(offset / 20) + 1

  const tabs = [
    { id: 'all' as const, label: 'All' },
    { id: 'active' as const, label: 'Active' },
    { id: 'expiring_soon' as const, label: 'Expiring Soon' },
    { id: 'expired' as const, label: 'Expired' },
  ]

  const filtered = pucs.filter((p: any) =>
    !debouncedSearch ||
    p.customer_name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    p.certificate_number?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    p.vehicle_reg?.toLowerCase().includes(debouncedSearch.toLowerCase())
  )

  const handleAddPuc = () => {
    setShowCustomerPicker(true)
  }

  const handleEditPuc = (puc: PUC) => {
    setSelectedPuc(puc)
    setSelectedCustomerId(puc.customer_id)
    setShowAddModal(true)
  }

  const handleCloseModal = () => {
    setShowAddModal(false)
    setSelectedPuc(undefined)
    setSelectedCustomerId('')
  }

  return (
    <>
      <PageHeader title="PUCs" action={{ label: 'Add PUC', onClick: handleAddPuc }} />

      {/* Search and Filter */}
      {/* <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by customer, cert #, or vehicle..."
            className="w-full rounded-lg border border-gray-200 pl-10 pr-4 py-2 text-sm focus:border-saffron focus:outline-none"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setOffset(0)
            }}
          />
        </div>
      </div> */}

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setStatus(tab.id); setOffset(0) }}
            className={`px-4 py-3 font-medium border-b-2 transition-colors ${
              status === tab.id
                ? 'border-saffron text-saffron'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-gray-500">
            <div className="text-center">
              <p className="mb-4">No PUCs found</p>
              <Button onClick={handleAddPuc} className="bg-saffron">
                <Plus className="w-4 h-4 mr-2" />
                Create PUC
              </Button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4">Customer</th>
                  <th className="text-left py-3 px-4">Vehicle</th>
                  <th className="text-left py-3 px-4">Cert #</th>
                  <th className="text-left py-3 px-4">Test Date</th>
                  <th className="text-left py-3 px-4">Expiry</th>
                  <th className="text-left py-3 px-4">Result</th>
                  <th className="text-left py-3 px-4">Center</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.map((puc: any) => (
                  <tr key={puc.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <button onClick={() => navigate(`/customers/${puc.customer_id}`)} className="text-saffron hover:underline">
                        {puc.customer_name}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-sm">{puc.vehicle_reg}</td>
                    <td className="py-3 px-4 font-medium">{puc.certificate_number}</td>
                    <td className="py-3 px-4">{formatDate(puc.test_date)}</td>
                    <td className="py-3 px-4"><ExpiryBadge days={daysRemaining(puc.expiry_date)} /></td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-1 rounded ${puc.result === 'pass' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {puc.result}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">{puc.test_center || '-'}</td>
                    <td className="py-3 px-4 text-right flex justify-end gap-2">
                      <Button size="sm" variant="secondary" onClick={() => handleEditPuc(puc)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setDeleteId(puc.id)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && filtered.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">Showing {Math.min(offset + 20, total)} of {total}</p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - 20))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setOffset(offset + 20)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm">
            <p className="text-gray-900 font-medium mb-4">Delete this PUC?</p>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setDeleteId(null)}>Cancel</Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => deleteMutation.mutate(deleteId)}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Picker */}
      {showCustomerPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Customer</h3>
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-4 py-2 mb-3 focus:border-saffron focus:outline-none"
              disabled={customersLoading}
              autoFocus
            />
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              {filteredCustomers.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {customerSearch ? 'No customers found' : 'No customers available'}
                </div>
              ) : (
                filteredCustomers.map((customer) => (
                  <button
                    key={customer.id}
                    onClick={() => {
                      setSelectedCustomerId(customer.id)
                      setShowCustomerPicker(false)
                      setShowAddModal(true)
                    }}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-saffron/5 transition-colors ${
                      selectedCustomerId === customer.id ? 'bg-saffron/10' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900">{customer.full_name}</div>
                    <div className="text-sm text-gray-500">{customer.phone}</div>
                  </button>
                ))
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button 
                variant="secondary" 
                onClick={() => { 
                  setShowCustomerPicker(false)
                  setSelectedCustomerId('')
                  setCustomerSearch('')
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit PUC Modal */}
      <AddPUCModal
        open={showAddModal}
        onClose={handleCloseModal}
        customerId={selectedCustomerId}
        puc={selectedPuc}
        onSuccess={() => {
          handleCloseModal()
          queryClient.invalidateQueries({ queryKey: ['pucs'] })
        }}
      />
    </>
  )
}
